// Founder's Achievement Celebration.
//
// Fires an enchanted gold particle burst plus a V2 True Glass badge every
// time the caller's referral count crosses one of the milestones (1, 2,
// or 3). Uses `canvas-confetti` with two synchronised waves: glowing gold
// shards that spiral upward from the bottom, then champagne orbs that
// rain gently back down. World of Warcraft enchant vibes.
//
// The last celebrated count is persisted in localStorage so a page
// refresh does not re-fire. Idempotent by design.

import { useCallback, useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Sparkles, Crown, Trophy, X } from "lucide-react";

const STORAGE_KEY = "cs:last-celebrated-referrals";
const MILESTONES = [3, 5, 8, 10];

const MILESTONE_TITLES = {
  3:  "Strategic Alliance Confirmed. 3/3 Foundations Set.",
  5:  "Strategic Alliance Expanded. 5/5 Kitchens Confirmed.",
  8:  "Strategic Alliance Deepened. 8/8 Kitchens Confirmed.",
  10: "Founder's Circle Unlocked. Lifetime Precision Secured.",
};

const MILESTONE_SUBTITLES = {
  3:  "Three kitchens have matured under your standard. Your Founder's Circle badge is now permanent on this account.",
  5:  "Five matured referrals. Your voice is now shaping five real operations.",
  8:  "Eight matured referrals. Your circle now runs at the standard you set.",
  10: "Ten matured referrals. The lifetime precision reward is now locked in on this account.",
};

function readLastCelebrated() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null || raw === undefined) return 0;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function writeLastCelebrated(n) {
  try { localStorage.setItem(STORAGE_KEY, String(n)); } catch { /* noop */ }
}

// Custom shard shape: a tall, faceted diamond that reads as a gold shard.
const SHARD_SHAPE = confetti.shapeFromPath({
  path: "M50 0 L100 50 L50 100 L0 50 Z",
  matrix: [0.6, 0, 0, 0, 1.6, 0, 0, 0, 0.6, 0, 0, 0, 0, 0, 0, 1],
});

function launchEnchantedBurst(intensity = 1) {
  // Wave 1: glowing gold shards spiral upward from both bottom corners.
  const shardOpts = {
    particleCount: Math.round(80 * intensity),
    spread: 70,
    startVelocity: 55,
    ticks: 260,
    gravity: 0.55,
    scalar: 1.15,
    shapes: [SHARD_SHAPE],
    colors: ["#D4AF37", "#F5D97C", "#FFF3B0", "#FFF8E1"],
  };
  confetti({ ...shardOpts, origin: { x: 0.15, y: 1.05 }, angle: 70 });
  confetti({ ...shardOpts, origin: { x: 0.85, y: 1.05 }, angle: 110 });

  // Wave 2 (delayed): champagne orbs float from top-center down like petals.
  setTimeout(() => {
    confetti({
      particleCount: Math.round(120 * intensity),
      spread: 140,
      startVelocity: 22,
      ticks: 420,
      gravity: 0.35,
      scalar: 0.9,
      shapes: ["circle"],
      colors: ["#FFF8E1", "#FFF3B0", "#D4AF37", "#E8CF9A"],
      origin: { x: 0.5, y: 0.2 },
      drift: 0.6,
    });
  }, 380);

  // Wave 3 (finale): centre burst of tiny shards + orbs.
  setTimeout(() => {
    confetti({
      particleCount: Math.round(60 * intensity),
      spread: 360,
      startVelocity: 30,
      ticks: 220,
      gravity: 0.4,
      scalar: 0.75,
      shapes: ["circle", SHARD_SHAPE],
      colors: ["#D4AF37", "#FFF8E1", "#F5D97C"],
      origin: { x: 0.5, y: 0.5 },
    });
  }, 900);
}


export default function FoundersCelebration() {
  const { user } = useAuth();
  const [milestone, setMilestone] = useState(null); // 1, 2, or 3
  const [visible, setVisible] = useState(false);
  const polledRef = useRef(false);

  const check = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/founders/status");
      // Use matured_count exclusively. Pending referrals no longer trigger
      // the enchanted gold moment. Only truly Active Maturity crossings do.
      const matured = Number(data?.matured_count ?? data?.referrals_count ?? 0);
      const last = readLastCelebrated();
      const crossed = MILESTONES.filter((m) => matured >= m && last < m);
      if (crossed.length > 0) {
        const target = crossed[crossed.length - 1];
        setMilestone(target);
        setVisible(true);
        writeLastCelebrated(target);
        setTimeout(() => launchEnchantedBurst(target >= 10 ? 1.6 : target >= 5 ? 1.2 : 1.0), 120);
      }
    } catch {
      // silent
    }
  }, [user]);

  // On mount + on window focus. Cheap: single GET each time.
  useEffect(() => {
    if (!user || polledRef.current) return;
    polledRef.current = true;
    check();
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    // Also listen for an explicit event so ShareModal or any future
    // realtime channel can force a re-check after a share confirmation.
    window.addEventListener("cs:founders-updated", check);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("cs:founders-updated", check);
    };
  }, [user, check]);

  if (!visible || !milestone) return null;

  const isFinal = milestone === 10;
  const IconEl = isFinal ? Crown : milestone >= 5 ? Trophy : Sparkles;

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center px-4"
      style={{ background: "rgba(4,3,2,0.62)", backdropFilter: "blur(8px)" }}
      data-testid="founders-celebration"
      onClick={() => setVisible(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-[520px] rounded-3xl p-8 text-center"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 55%, rgba(255,255,255,0.015) 100%)",
          backdropFilter: "blur(44px) saturate(200%)",
          WebkitBackdropFilter: "blur(44px) saturate(200%)",
          border: "1px solid rgba(212,175,55,0.55)",
          boxShadow: [
            "inset 0 1px 0 rgba(255,255,255,0.32)",
            "0 0 40px #d4af37aa",
            "0 30px 80px -20px rgba(0,0,0,0.9)",
          ].join(","),
          animation: "founders-celebration-in 700ms cubic-bezier(0.2, 0.85, 0.3, 1.15) both",
        }}
      >
        {/* Prism hairline */}
        <div
          aria-hidden
          className="absolute top-0 left-6 right-6 h-px rounded-full pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, transparent, #D4AF37, #FFF8E1, #D4AF37, transparent)",
          }}
        />

        <button
          onClick={() => setVisible(false)}
          data-testid="founders-celebration-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)" }}
          aria-label="Close celebration"
        >
          <X className="w-4 h-4 text-white/70" />
        </button>

        <div
          className="mx-auto mb-5 flex items-center justify-center rounded-full"
          style={{
            width: 88,
            height: 88,
            background:
              "radial-gradient(circle at 30% 30%, #FFF8E1 0%, #F5D97C 45%, #8A6B1A 100%)",
            boxShadow:
              "0 0 40px #d4af37aa, 0 0 12px rgba(255,248,225,0.6), inset 0 2px 0 rgba(255,255,255,0.4)",
          }}
        >
          <IconEl className="w-10 h-10" style={{ color: "#3a2c07" }} strokeWidth={2.2} />
        </div>

        <div
          className="label-eyebrow gold mb-3"
          style={{ letterSpacing: "0.32em", textShadow: "0 0 10px rgba(212,175,55,0.55)" }}
        >
          {isFinal ? "Founder's Circle Sealed" : `Matured Milestone ${milestone}`}
        </div>
        <h2
          className="font-display leading-[1.1] mb-3"
          style={{
            fontSize: "1.9rem",
            fontWeight: 800,
            letterSpacing: "-0.015em",
            textShadow: "0 0 18px rgba(212,175,55,0.55)",
          }}
          data-testid="founders-celebration-title"
        >
          {MILESTONE_TITLES[milestone]}
        </h2>
        <p className="text-[0.95rem] text-white/78 leading-relaxed mb-6">
          {MILESTONE_SUBTITLES[milestone]}
        </p>

        <button
          onClick={() => setVisible(false)}
          data-testid="founders-celebration-cta"
          className="inline-flex items-center gap-2 rounded-full font-semibold px-5 py-2.5 text-sm transition-transform hover:scale-[1.03]"
          style={{
            background:
              "linear-gradient(180deg, rgba(212,175,55,0.85), rgba(200,169,106,0.5))",
            border: "1px solid rgba(255,248,225,0.55)",
            color: "#1c1305",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.55), 0 0 24px #d4af37aa",
          }}
        >
          <Sparkles className="w-4 h-4" /> Continue the standard
        </button>
      </div>

      <style>{`
        @keyframes founders-celebration-in {
          0%   { opacity: 0; transform: translateY(24px) scale(0.9); }
          60%  { opacity: 1; transform: translateY(-6px) scale(1.03); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
